import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from './Button';

/**
 * ErrorBoundary Component
 *
 * React Error Boundary that catches JavaScript errors in child components,
 * logs the errors, and displays a fallback UI.
 *
 * Features:
 * - Catches rendering errors
 * - Logs error details to console
 * - Shows user-friendly fallback UI
 * - Retry functionality
 * - Optional custom fallback component
 *
 * @example
 * <ErrorBoundary fallback={<CustomError />}>
 *   <ComponentThatMightError />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      errorInfo,
    });

    // Optional: Send error to logging service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg">
          <div className="rounded-full bg-red-100 p-3 mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {this.props.errorTitle || 'Something went wrong'}
          </h3>

          <p className="text-sm text-gray-600 text-center mb-4 max-w-md">
            {this.props.errorMessage ||
              'An unexpected error occurred while displaying this content. Please try again.'}
          </p>

          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mb-4 w-full max-w-md">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                Error Details (dev only)
              </summary>
              <pre className="mt-2 text-xs bg-white p-3 rounded border border-gray-300 overflow-auto max-h-40">
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}

          <div className="flex gap-3">
            {this.props.showRetry !== false && (
              <Button variant="primary" onClick={this.handleReset}>
                Try Again
              </Button>
            )}

            {this.props.onGoBack && (
              <Button variant="secondary" onClick={this.props.onGoBack}>
                Go Back
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  showRetry: PropTypes.bool,
  onError: PropTypes.func,
  onReset: PropTypes.func,
  onGoBack: PropTypes.func,
};

ErrorBoundary.defaultProps = {
  showRetry: true,
};

export default ErrorBoundary;
